import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { 
  updateSelectedTeam, 
  updateAvailableTeams, 
  setLoading, 
  setProvisioning,
  setGrantingAccess,
  setError,
  clearError,
  clearSelection,
  addNewTeam,
  updateTeamName as updateTeamNameAction,
  setCreationModalOpen
} from './slice';
import { TeamResult } from '../../../interfaces/users';
import { getServicesManager } from '../../../services';
import { ProvisionedTeam } from '../../../interfaces/provisioned';

// Force TypeScript recompilation
export function useProvisionedTeams(): {
  selectedTeamId: string | null;
  selectedTeam: ProvisionedTeam | undefined;
  selectedTeamName: string;
  availableTeams: ProvisionedTeam[];
  isLoading: boolean;
  isProvisioning: boolean;
  error: string | null;
  hasTeams: boolean;
  isCreationModalOpen: boolean;
  loadTeams: () => Promise<void>;
  selectTeam: (teamId: string) => void;
  createNewTeam: (teamName: string) => Promise<TeamResult | null>;
  updateTeamName: (teamId: string, newName: string) => Promise<boolean | null>;
  initialize: () => void;
  clearTeamSelection: () => void;
  dismissError: () => void;
  openCreationModal: () => void;
  closeCreationModal: () => void;
  isReady: boolean;
  hasError: boolean;
} {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.provisionedTeamSelector);
  const canConfigure = useSelector((state: RootState) => state.user.can_configure)
  const canProvision = useSelector((state: RootState) => state.user.can_provision)
  const team = useSelector((state: RootState) => state.team.value)

  // Get selected team details
  const selectedTeam = state.availableTeams.find(team => team.team_id === state.selectedTeamId);
  const selectedTeamName = selectedTeam?.team_name || (team && state.selectedTeamId === team.team_id ? team.team : 'Select Team')
  const user = useSelector((state: RootState) => state.user);

  // Load teams
  const loadTeams = useCallback(async () => {
    dispatch(setLoading(true));
    
    try {
      const teams = await getServicesManager().getProvisionedTeams();
      if (teams !== null) {
        dispatch(updateAvailableTeams(teams));
      } else {
        dispatch(setError('Failed to load teams'));
      }
    } catch (error) {
      dispatch(setError('Failed to load teams'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!user || !user.value) return
    if (!user.can_configure && !user.can_provision) return
    if (canProvision) return
    if (!canConfigure) return
    dispatch(updateSelectedTeam(user.value.team_id));
    // On mount, load teams if user can configure or provision
  }, [dispatch, user]);

  // Select team
  const selectTeam = useCallback((teamId: string) => {
    dispatch(updateSelectedTeam(teamId))
  }, [dispatch]);


  // Create new team with multidialer setup
  const createNewTeam = useCallback(async (teamName: string): Promise<TeamResult | null> => {
    dispatch(setProvisioning(true));
    dispatch(clearError());
    
    try {
      // Set telephonySeats=null (unlimited) to include Twilio subscription setup
      const newTeam = await getServicesManager().provisionNewTeam(teamName, {telephonySeats: null});
      
      if (newTeam !== null) {
        // Convert TeamResult to ProvisionedTeam format
        const provisionedTeam: ProvisionedTeam = {
          team_id: newTeam.team_id,
          team_name: newTeam.team,
        };
        
        dispatch(addNewTeam(provisionedTeam));
      } else {
        dispatch(setError('Failed to create team. Please check your permissions and try again.'));
      }
      
      dispatch(setProvisioning(false));
      return newTeam;
    } catch (error) {
      console.error('Team provisioning error:', error);
      dispatch(setError('Failed to create team. Please check your network connection and try again.'));
      dispatch(setProvisioning(false));
      return null;
    }
  }, [dispatch]);

  // Update team name
  const updateTeamName = useCallback(async (teamId: string, newName: string) => {
    try {
      const success = await getServicesManager().updateProvisionTeam(teamId, newName);
      if (success) {
        dispatch(updateTeamNameAction({ teamId, newName }));
      } else {
        dispatch(setError('Failed to update team name'));
      }
      return success;
    } catch (error) {
      dispatch(setError('Failed to update team name'));
      return null;
    }
  }, [dispatch]);

  // Initialize - just load teams
  const initialize = useCallback(() => {
    loadTeams();
  }, [loadTeams]);

  // Clear selection
  const clearTeamSelection = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  // Dismiss error
  const dismissError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Open creation modal
  const openCreationModal = useCallback(() => {
    dispatch(setCreationModalOpen(true));
  }, [dispatch]);

  // Close creation modal
  const closeCreationModal = useCallback(() => {
    dispatch(setCreationModalOpen(false));
  }, [dispatch]);

  return {
    // State
    selectedTeamId: state.selectedTeamId,
    selectedTeam,
    selectedTeamName,
    availableTeams: state.availableTeams,
    isLoading: state.isLoading,
    isProvisioning: state.isProvisioning,
    error: state.error,
    hasTeams: state.availableTeams.length > 0,
    isCreationModalOpen: state.isCreationModalOpen,
    
    // Actions
    loadTeams,
    selectTeam,
    createNewTeam,
    updateTeamName,
    initialize,
    clearTeamSelection,
    dismissError,
    openCreationModal,
    closeCreationModal,
    
    // Computed state
    isReady: !state.isLoading && !state.isProvisioning,
    hasError: Boolean(state.error),
  };
} 